import React from 'react';
import {
	useSelector
} from 'react-redux';
import scrollbarSize from 'dom-helpers/scrollbarSize';

import type CK from '~/types/contentking';

import BasicIcon, {
	BasicIconType
} from '~/components/patterns/icons/BasicIcon';
import DataCategoryCell from '~/components/atoms/boxes/DataCategoryCell';
import ColumnFilter from '../filters/ColumnFilter';
import ColumnName from '~/components/names/ColumnName';
import ExpandableFilterOperatorHeaderLabel from '../filters/ExpandableFilterOperatorHeaderLabel';
import FilterFieldLayout from '~/components/patterns/filtering/FilterFieldLayout';
import HealthIcon from '~/components/patterns/icons/HealthIcon';
import Form from '~/components/atoms/forms/basis/Form';
import List, {
	ListSize
} from '~/components/patterns/lists/List';
import TableLabel from '~/components/patterns/tables/datatables/parts/TableLabel';

import useGetFilterType from '~/hooks/useGetFilterType';
import useWebsiteCustomElementDefinitions from '~/hooks/useWebsiteCustomElementDefinitions';
import useWebsiteSegmentDefinitions from '~/hooks/useWebsiteSegmentDefinitions';

import {
	AllCategories,
	PagesColumnsCategory,
	getColumnCategory
} from '~/model/pagesColumns';

import {
	FILTER_TYPE_STRING,
	FILTER_TYPE_STRING_NON_NULLABLE
} from '~/model/pagesColumnsFiltering';

import {
	filterSelector
} from '~/state/ui/selectors';
import {
	notEmpty
} from '~/utilities/typeCheck';



type Props = {
	columns: ReadonlyArray<CK.PagesColumn>,
	websiteId: CK.WebsiteId,
	onFilterChange: (filter: Record<string, any>) => void,
};

const PagesGraphsSidebar: React.FC<Props> = (props) => {
	const {
		columns,
		onFilterChange,
		websiteId,
	} = props

	const getFilterType = useGetFilterType();

	const filter = useSelector(filterSelector);
	const customElementDefinitions = useWebsiteCustomElementDefinitions(websiteId);
	const segmentDefinitions = useWebsiteSegmentDefinitions(websiteId);

	const changeCallbackTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
	const rerenderFieldTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

	const filterFieldsRef = React.useRef<Record<string, any>>({});
	const previousFilterRef = React.useRef<any>();

	const handleFormChange = React.useCallback(
		(field, value) => {
			if (changeCallbackTimeoutRef.current) {
				clearTimeout(changeCallbackTimeoutRef.current);
				changeCallbackTimeoutRef.current = undefined;
			}

			if (rerenderFieldTimeoutRef.current) {
				clearTimeout(rerenderFieldTimeoutRef.current);
				rerenderFieldTimeoutRef.current = undefined;
			}

			changeCallbackTimeoutRef.current = setTimeout(() => {
				if (rerenderFieldTimeoutRef.current) {
					clearTimeout(rerenderFieldTimeoutRef.current);
					rerenderFieldTimeoutRef.current = undefined;
				}

				rerenderFieldTimeoutRef.current = setTimeout(() => {
					rerenderFieldTimeoutRef.current = undefined;
				}, 500);

				onFilterChange({
					[field]: value === undefined ? '' : value,
				});

				changeCallbackTimeoutRef.current = undefined;
			}, 500);

			rerenderFieldTimeoutRef.current = setTimeout(() => {
				rerenderFieldTimeoutRef.current = undefined;
			}, 500);
		},
		[
			onFilterChange,
		],
	);


	React.useEffect(
		() => {
			if (!filter) {
				return;
			}

			filter.forEach((value, field) => {
				if (!previousFilterRef.current || previousFilterRef.current.get(field) !== value) {
					if (
						rerenderFieldTimeoutRef.current === undefined
						&& filterFieldsRef.current[field]?.current !== undefined
					) {
						if (filterFieldsRef.current[field].current?.updateFilterValue) {
							filterFieldsRef.current[field].current.updateFilterValue(value);
						} else if (filterFieldsRef.current[field].current?.changeValue) {
							filterFieldsRef.current[field].current.changeValue(value);
						}
					}
				}
			});

			previousFilterRef.current?.forEach((value, field) => {
				if (!filter.has(field)) {
					if (
						rerenderFieldTimeoutRef.current === undefined
						&& filterFieldsRef.current[field]?.current !== undefined
					) {
						if (filterFieldsRef.current[field].current?.updateFilterValue) {
							filterFieldsRef.current[field].current.updateFilterValue(null);
						} else if (filterFieldsRef.current[field].current?.changeValue) {
							filterFieldsRef.current[field].current.changeValue(null);
						}
					}
				}
			});

			previousFilterRef.current = filter;
		},
		[
			filter,
		],
	);

	const content = AllCategories.map((category, index) => {
		const categoryColumns = columns.filter((column) => getColumnCategory(column) === category)

		if (categoryColumns.length === 0) {
			return null;
		}

		if (category === PagesColumnsCategory.CustomElements) {
			if (!customElementDefinitions.isLoaded) {
				return null;
			}
		}

		return (
			<DataCategoryCell
				key={'cell-' + index}
				style={category}
			>
				<List size={ListSize.XLarge}>
					{categoryColumns.map((columnName) => {
						filterFieldsRef.current[columnName] ??= {
							current: undefined,
						};

						const fieldWidth = 330 - 40 - scrollbarSize();

						const icon = (
							columnName === 'segments' ? (
								<BasicIcon
									size={14}
									type={BasicIconType.Segment}
								/>
							) : columnName === 'health' ? (
								<HealthIcon />
							) : null
						);


						const filterType = getFilterType(columnName);
						const labelOperator = (
							(filterType === FILTER_TYPE_STRING || filterType === FILTER_TYPE_STRING_NON_NULLABLE) ? (
								<ExpandableFilterOperatorHeaderLabel
									value={filter.get(columnName)}
								/>
							) : null
						);

						return (
							<FilterFieldLayout
								field={(
									<ColumnFilter
										columnName={columnName}
										columnWidth={fieldWidth}
										isAtRightEdge={true}
										ref={filterFieldsRef.current[columnName]}
										segmentDefinitions={segmentDefinitions.listAll()}
									/>
								)}
								key={columnName}
								label={(
									<TableLabel
										label={(
											<ColumnName column={columnName} />
										)}
										labelIcon={icon}
										labelOperator={labelOperator}
									/>
								)}
							/>
						);
					})}
				</List>
			</DataCategoryCell>
		);
	}).filter(notEmpty);

	if (content.length === 0) {
		return null;
	}

	return (
		<Form
			defaultValues={filter ? filter.toObject() : {}}
			onChangeCallback={handleFormChange}
		>
			{content}
		</Form>
	);
};



export default PagesGraphsSidebar;
